<template>
    <div class="date-picker input-component" :class="cssClasses">
        <v-menu
            ref="menuRef"
            v-model="datePickerIsVisible"
            location="bottom right"
            :min-width="calendarWidth"
            :width="calendarWidth"
            :close-on-content-click="false"
            :open-on-click="false"
        >
            <template #activator="{ props: activatorProps }">
                <v-text-field
                    v-bind="{ ...props, ...activatorProps }"
                    v-model="rawInputValue"
                    v-model:focused="innerFocused"
                    :type="isMobileDevice ? 'date' : undefined"
                    :value="isMobileDevice ? mobileInputValue : rawInputValue"
                    :min="min"
                    :max="max"
                    @change="updateInputValue"
                    @update:model-value.passive="updateInputValue"
                >
                    <template v-if="!isMobileDevice" #append-inner>
                        <v-icon icon="mdi-calendar-month-outline" size="large" class="date-picker__icon" @click="showDatePicker" />
                    </template>
                    <template v-if="isMobileDevice" #default>
                        <div class="date-picker__mobile-display">
                            <div class="flex-1-0">{{ inputValue }}</div>
                            <v-icon icon="mdi-calendar-month-outline" size="large" class="date-picker__icon" />
                        </div>
                    </template>
                </v-text-field>
            </template>
            <v-card :width="calendarWidth" class="date-picker__dialog" :class="textColorClasses">
                <v-date-picker
                    v-model="dateValue"
                    :min="min"
                    :max="max"
                    elevation="0"
                    :color="color"
                    hide-header
                    show-adjacent-months
                    @update:model-value="hideDatePicker"
                />
            </v-card>
        </v-menu>
    </div>
</template>

<script setup lang="ts">
import { useElementSize } from '@vueuse/core';
import { formatDate, isValid, startOfDay, set, parse, isBefore, isAfter } from 'date-fns';
import { computed, nextTick, ref, watch } from 'vue';
import type { VMenu } from 'vuetify/components';
import { useBaseInputComponent, type BaseInputComponentProps } from '../BaseInputComponent';
// @ts-ignore
import { useTextColor } from 'vuetify/lib/composables/color.mjs';

interface Props extends BaseInputComponentProps {
    /**
     * Format of the date. See https://date-fns.org/v3.6.0/docs/format
     * @default 'dd.MM.yyyy'
     */
    format?: string;

    /**
     * Minimum allowed date
     */
    min?: string;

    /**
     * Maximum allowed date
     */
    max?: string;

    /**
     * Width of the calendar
     * @default 320
     */
    calendarWidth?: number;
}

const props = withDefaults(defineProps<Props>(), { calendarWidth: 320, format: 'dd.MM.yyyy' });
const modelValue = defineModel<Date | number>({ default: () => new Date(), required: true });
const focused = defineModel<boolean>('focused');
const { isMobileDevice, cssClasses: baseCssClasses, watchInputClass } = useBaseInputComponent(props);
const cssClasses = computed(() => ({
    ...baseCssClasses.value,
    'input-component--focused': focused.value
}));
const innerFocused = computed({
    get() {
        return focused.value || datePickerIsVisible.value;
    },
    set(value) {
        focused.value = value;
    }
});

const dateValue = computed({
    get() {
        return startOfDay(modelValue.value);
    },
    set(value) {
        modelValue.value = set(modelValue.value, {
            year: value.getFullYear(),
            month: value.getMonth(),
            date: value.getDate()
        });
    }
});
const inputValue = computed({
    get() {
        return formatDate(dateValue.value, props.format);
    },
    set(value) {
        const parsedDate = parse(value, props.format, dateValue.value);
        if (isValid(parsedDate)) {
            dateValue.value = parsedDate;
        }
    }
});
const rawInputValue = ref(inputValue.value);

const menuRef = ref<InstanceType<typeof VMenu>>();
const activatorEl = computed(() => menuRef.value?.activatorEl);
const { width: inputWidth } = useElementSize(activatorEl, undefined, { box: 'border-box' });
const calendarWidth = computed(() => Math.max(320, inputWidth.value, props.calendarWidth));

const datePickerIsVisible = ref(false);
const { textColorClasses } = useTextColor(computed(() => props.color));

// used to format the time value for mobile devices, which must be in HH:mm format
const mobileInputValue = computed(() => formatDate(dateValue.value, 'yyyy-MM-dd'));

// check if the input value is within the min and max times
function isWithinAllowedRange(value: Date) {
    // compare input value with min and max times
    if (props.min) {
        const parsedMin = parse(props.min, 'yyyy-MM-dd', value);
        if (isValid(parsedMin) && isBefore(value, parsedMin)) return false;
    }

    if (props.max) {
        const parsedMax = parse(props.max, 'yyyy-MM-dd', value);
        if (isValid(parsedMax) && isAfter(value, parsedMax)) return false;
    }

    return true;
}

function updateInputValue() {
    const parsedDate = parse(rawInputValue.value, isMobileDevice.value ? 'yyyy-MM-dd' : props.format, dateValue.value);

    if (isValid(parsedDate) && isWithinAllowedRange(parsedDate)) {
        dateValue.value = parsedDate;
    } else {
        // reset the input value to the time value
        rawInputValue.value = inputValue.value;
    }
}

function showDatePicker() {
    if (isMobileDevice.value) return;

    datePickerIsVisible.value = true;
}

function hideDatePicker() {
    datePickerIsVisible.value = false;
}

// update the input value when the model value changes from outside the component
watch(dateValue, () => nextTick(() => (rawInputValue.value = inputValue.value)));

watchInputClass(activatorEl);
</script>

<style lang="scss">
.date-picker {
    &__dialog {
        border: 1px solid currentColor;

        .v-date-picker {
            width: 100% !important;
        }
    }

    .v-date-picker-month {
        padding: 0 10px 10px;
    }

    .v-date-picker-month__day {
        height: 44px;
        width: 44px;
    }

    .v-field__input {
        text-align: center;
    }

    // hide the input field when the mobile display is active
    // but retain the input field for native mobile time input
    &:has(.date-picker__mobile-display) .v-field__input input {
        opacity: 0;
    }

    // facade for the mobile display to make it looks like a normal input field
    &__mobile-display {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        text-align: center;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1;
        pointer-events: none;
        min-height: max(
            var(--v-input-control-height, 56px),
            1.5rem + var(--v-field-input-padding-top) + var(--v-field-input-padding-bottom)
        );
        min-width: 0;
        padding-inline: var(--v-field-padding-start) var(--v-field-padding-end);
        padding-top: var(--v-field-input-padding-top);
        padding-bottom: var(--v-field-input-padding-bottom);

        .date-picker__icon {
            opacity: var(--v-medium-emphasis-opacity);
        }
    }
}
</style>
